import React, { createContext, useContext, useEffect, ReactNode, useState, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { Appointment } from '../types';
import { useAuth } from './AuthContext';
import safeStorage from '../utils/safeStorage';
import { SOCKET_URL } from '../config/env';



const SOCKET_EVENTS = [
  'connect',
  'connect_error',
  'disconnect',
  'appointment_created',
  'appointment_updated',
  'appointment_deleted',
  'mechanic_appointment_updated',
  // Payment events: pages subscribe via `socket.on` themselves; listed here
  // so provider teardown off()s them too.
  'payment_updated',
  'invoice_updated',
  'square_connection_updated',
];

interface SocketContextProps {
  socket: Socket | null;
  connected: boolean;
  appointments: Appointment[];
  error: string | null;
}

interface SocketProviderProps {
  children: ReactNode;
}

const SocketContext = createContext<SocketContextProps | undefined>(undefined);

export const useSocket = (): SocketContextProps => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { user, isAuthenticated } = useAuth();
  const socketRef = useRef<Socket | null>(null);

  // Remove all listeners before disconnecting so reconnects don't stack handlers
  const cleanupSocket = (socketInstance: Socket | null) => {
    if (!socketInstance) return;
    try {
      SOCKET_EVENTS.forEach((event) => socketInstance.off(event));
      socketInstance.disconnect();
    } catch (err) {
      console.error('Error during socket cleanup:', err);
    }
  };

  useEffect(() => {
    if (socketRef.current) {
      cleanupSocket(socketRef.current);
      socketRef.current = null;
      setSocket(null);
    }

    const token = safeStorage.getItem('token');

    // The gateway rejects anonymous connections; rooms are assigned server-side
    // from the token, so there is nothing to join from here.
    if (!isAuthenticated || !token) {
      setConnected(false);
      setAppointments([]);
      return;
    }

    const socketInstance = io(SOCKET_URL, {
      transports: ['websocket'],
      auth: { token },
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 10000,
      autoConnect: false,
    });

    socketRef.current = socketInstance;

    socketInstance.on('connect', () => {
      setConnected(true);
      setError(null);
    });

    socketInstance.on('connect_error', (err) => {
      console.error('Socket connection error:', err);
      setError('Failed to connect to notification service');
      setConnected(false);
    });

    socketInstance.on('disconnect', () => {
      setConnected(false);
    });

    socketInstance.on('appointment_created', (appointment: Appointment) => {
      setAppointments((prev) =>
        prev.some((app) => app.id === appointment.id) ? prev : [...prev, appointment]
      );
    });

    const applyUpdate = (updatedAppointment: Appointment) => {
      setAppointments((prev) =>
        prev.map((app) => (app.id === updatedAppointment.id ? updatedAppointment : app))
      );
    };

    socketInstance.on('appointment_updated', applyUpdate);
    socketInstance.on('mechanic_appointment_updated', applyUpdate);

    socketInstance.on('appointment_deleted', ({ id }: { id: string }) => {
      setAppointments((prev) => prev.filter((app) => app.id !== id));
    });

    try {
      socketInstance.connect();
      setSocket(socketInstance);
    } catch (connectErr) {
      console.error('Error connecting to socket:', connectErr);
      setError('Failed to connect to notification service');
      setConnected(false);
      cleanupSocket(socketInstance);
      socketRef.current = null;
    }

    return () => {
      if (socketRef.current) {
        cleanupSocket(socketRef.current);
        socketRef.current = null;
      }
    };
  }, [isAuthenticated, user?.id, user?.role]);

  return (
    <SocketContext.Provider value={{ socket, connected, appointments, error }}>
      {children}
    </SocketContext.Provider>
  );
};
